import React from 'react';
import { Link } from 'react-router-dom';
import { Workflow, Globe, Smartphone, Bot, Compass, Plug, Zap, Cloud } from 'lucide-react';

// Coordenadas en un lienzo de referencia de 700x460 -- se posicionan como
// porcentaje para que el diagrama escale sin desalinear los conectores SVG.
// Los 8 nodos se ubican en un octágono alrededor del hub (mismo estilo que
// el diagrama de referencia), cada uno con su propio conector en ángulo recto.
const CANVAS = { w: 700, h: 460 };
const HUB = { x: 350, y: 230 };
const BADGE = 56; // tamaño del ícono en px, para calcular dónde arranca cada línea

const NODES = [
  { label: 'Aplicaciones web', icon: Globe, x: 254, y: 66 },
  { label: 'Aplicaciones móviles', icon: Smartphone, x: 446, y: 66 },
  { label: 'Optimización de procesos', icon: Workflow, x: 87, y: 170 },
  { label: 'Despliegue de agentes IA', icon: Bot, x: 613, y: 170 },
  { label: 'Consultoría tecnológica', icon: Compass, x: 87, y: 290 },
  { label: 'Integraciones y APIs', icon: Plug, x: 613, y: 290 },
  { label: 'Automatización de tareas', icon: Zap, x: 254, y: 394 },
  { label: 'Infraestructura en la nube', icon: Cloud, x: 446, y: 394 },
];

// Circuito simple de fondo, en espejo respecto al de Servicios: baja por el
// borde derecho hasta el fondo de la sección, un nodo, y sigue horizontal
// hacia la izquierda. Mismo color y opacidad que el resto de circuitos.
const IndustriesCircuit = () => (
  <div className="absolute inset-0 z-0 pointer-events-none" aria-hidden="true">
    <div className="absolute right-10 top-0 bottom-0 w-[1.5px] bg-[#C7D2FE] opacity-70" />
    <div className="absolute right-10 left-0 bottom-0 h-[1.5px] bg-[#C7D2FE] opacity-70" />
    <div className="absolute right-[34px] bottom-0 w-[11px] h-[11px] rounded-full bg-[#A5B4FC] opacity-90" />
  </div>
);

const pct = (v, total) => `${(v / total) * 100}%`;

// Línea en ángulo recto: los nodos superiores/inferiores bajan/suben hasta la
// altura del hub y entran en línea recta; los nodos laterales entran hasta la
// columna del hub y luego suben/bajan -- igual que el diagrama de referencia.
const connectorPath = (x, y) => {
  const verticalDominant = Math.abs(x - HUB.x) < Math.abs(y - HUB.y);
  if (verticalDominant) {
    const startY = y < HUB.y ? y + BADGE / 2 : y - BADGE / 2;
    return `M ${x} ${startY} L ${x} ${HUB.y} L ${HUB.x} ${HUB.y}`;
  }
  const startX = x < HUB.x ? x + BADGE / 2 : x - BADGE / 2;
  return `M ${startX} ${y} L ${HUB.x} ${y} L ${HUB.x} ${HUB.y}`;
};

const MainframeIndustriesSection = () => {
  return (
    <section className="relative z-[1] bg-white px-5 sm:px-8 md:px-10 py-20 sm:py-28">
      <IndustriesCircuit />
      <div className="relative z-10 grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
        <div>
          <h2
            style={{ fontSize: 'clamp(26px, 4vw, 40px)', lineHeight: 1.15, fontWeight: 500, letterSpacing: '-0.01em' }}
            className="text-black mb-6"
          >
            Optimización de procesos y aplicaciones web y mobile para cualquier tipo de negocio
          </h2>
          <p className="text-black/60 text-[15px] sm:text-[16px] leading-relaxed mb-8 max-w-md">
            Orbiax se adapta a tu sector, sin importar el tamaño de tu empresa. Optimizamos tus
            procesos y desarrollamos aplicaciones web y móviles a la medida, incluyendo el
            despliegue de agentes de Inteligencia Artificial, con soporte técnico real detrás de
            cada implementación.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <a
              href="mailto:contacto@orbiax.co"
              className="inline-flex items-center gap-2 bg-black text-white text-[14px] font-medium px-6 py-3 rounded-full hover:opacity-80 transition-opacity"
            >
              Escríbenos
              <span aria-hidden="true">→</span>
            </a>
            <Link
              to="/servicios"
              className="inline-flex items-center gap-1 text-black text-[14px] font-medium hover:opacity-60 transition-opacity"
            >
              Ver servicios
              <span aria-hidden="true">›</span>
            </Link>
          </div>
        </div>

        {/* Debajo de md el canvas de 700x460 se aprieta demasiado (badges y
            etiquetas en px fijo empiezan a pisarse) -- se reemplaza por una
            grilla simple, sin conectores, que sí es legible en mobile. */}
        <div className="grid grid-cols-2 sm:grid-cols-4 md:hidden gap-x-4 gap-y-6">
          {NODES.map(({ label, icon: Icon }) => (
            <div key={label} className="flex flex-col items-center gap-2 text-center">
              <div className="rounded-xl bg-black flex items-center justify-center shrink-0 w-11 h-11">
                <Icon className="w-5 h-5 text-white" strokeWidth={1.75} />
              </div>
              <span className="text-black text-[12px] font-bold leading-tight">{label}</span>
            </div>
          ))}
        </div>

        <div className="hidden md:block relative w-full" style={{ aspectRatio: `${CANVAS.w} / ${CANVAS.h}` }}>
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox={`0 0 ${CANVAS.w} ${CANVAS.h}`}
            aria-hidden="true"
          >
            {NODES.map((node) => (
              <path
                key={node.label}
                d={connectorPath(node.x, node.y)}
                fill="none"
                stroke="#C7D2FE"
                strokeWidth="2"
              />
            ))}
          </svg>

          {NODES.map(({ label, icon: Icon, x, y }) => (
            <div
              key={label}
              className="absolute flex flex-col items-center gap-2.5 -translate-x-1/2 -translate-y-1/2"
              style={{ left: pct(x, CANVAS.w), top: pct(y, CANVAS.h), width: 116 }}
            >
              <div
                className="rounded-xl bg-black flex items-center justify-center shrink-0"
                style={{ width: BADGE, height: BADGE }}
              >
                <Icon className="w-6 h-6 text-white" strokeWidth={1.75} />
              </div>
              <span className="text-black text-[13px] font-bold text-center leading-tight">
                {label}
              </span>
            </div>
          ))}

          <div
            className="absolute rounded-2xl bg-white flex items-center justify-center -translate-x-1/2 -translate-y-1/2 shadow-xl border border-black/5"
            style={{ left: pct(HUB.x, CANVAS.w), top: pct(HUB.y, CANVAS.h), width: 84, height: 84 }}
          >
            <img src="/assets/orbiax-o.png" alt="Orbiax" className="w-11 h-11 object-contain" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default MainframeIndustriesSection;
